import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API } from "@/App";
import { CheckCircle, XCircle, Clock, Calendar, MapPin, CreditCard, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatTimeRange } from "@/utils/timeFormat";

const AcceptBooking = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState("loading"); // loading, success, error, already_processed
  const [message, setMessage] = useState("");
  const [bookingDetails, setBookingDetails] = useState(null);

  useEffect(() => {
    if (token) {
      acceptBooking();
    } else {
      setStatus("error");
      setMessage("Invalid link - no token provided");
    }
  }, [token]);

  const acceptBooking = async () => {
    try {
      const response = await fetch(`${API}/bookings/accept/${token}`);
      const data = await response.json();

      if (response.ok) {
        if (data.success) {
          setStatus("success");
          setMessage(data.message);
          setBookingDetails(data.booking);
        } else {
          setStatus("already_processed");
          setMessage(data.message);
        }
      } else {
        setStatus("error");
        setMessage(data.detail || "Failed to accept booking");
      }
    } catch (error) {
      setStatus("error");
      setMessage("An error occurred while processing your request");
    }
  };

  const getMeetingTypeIcon = (type) => {
    switch (type) {
      case "video":
        return <Video className="w-4 h-4" />;
      case "phone":
        return <span className="text-sm">📞</span>;
      default:
        return <MapPin className="w-4 h-4" />;
    }
  };

  const getPaymentLabel = (type) => {
    const labels = {
      "private-pay": "Private Pay",
      "cja": "CJA (Criminal Justice Act)",
      "state-court": "State Court"
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          {status === "loading" && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-blue-100 flex items-center justify-center">
                <Clock className="w-8 h-8 text-blue-600 animate-pulse" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Processing...</h1>
              <p className="text-slate-600">Please wait while we confirm your booking.</p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Booking Confirmed!</h1>
              <p className="text-slate-600 mb-6">{message}</p>
              
              {bookingDetails && (
                <div className="bg-slate-50 rounded-lg p-4 text-left mb-6">
                  <h3 className="font-semibold text-slate-900 mb-3">Booking Details</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-700">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span>{bookingDetails.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700">
                      <Clock className="w-4 h-4 text-slate-500" />
                      <span>{formatTimeRange(bookingDetails.start_time, bookingDetails.end_time)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700">
                      {getMeetingTypeIcon(bookingDetails.meeting_type)}
                      <span className="capitalize">{bookingDetails.meeting_type?.replace("-", " ")}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700">
                      <CreditCard className="w-4 h-4 text-slate-500" />
                      <span>{getPaymentLabel(bookingDetails.payment_type)}</span>
                    </div>
                    {bookingDetails.location && (
                      <div className="flex items-center gap-2 text-slate-700">
                        <MapPin className="w-4 h-4 text-slate-500" />
                        <span>{bookingDetails.location}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2 text-slate-700 pt-2 border-t border-slate-200 mt-2">
                      <span className="font-medium">Client:</span>
                      <span>{bookingDetails.client_name}</span>
                    </div>
                  </div>
                </div>
              )}
              
              <p className="text-sm text-slate-500 mb-4">
                The client has been notified of your confirmation.
              </p>
            </>
          )}

          {status === "already_processed" && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-amber-100 flex items-center justify-center">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Already Processed</h1>
              <p className="text-slate-600 mb-6">{message}</p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-8 h-8 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">Error</h1>
              <p className="text-slate-600 mb-6">{message}</p>
            </>
          )}

          <Link to="/login">
            <Button className="w-full bg-slate-900 hover:bg-slate-800 text-white">
              Go to Dashboard
            </Button>
          </Link>
        </div>
        
        <p className="text-center text-sm text-slate-500 mt-6">
          Book an Interpreter
        </p>
      </div>
    </div>
  );
};

export default AcceptBooking;
